import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Slider } from '../components/ui/slider';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import {
  BarChart3, Users, Target, TrendingUp, TrendingDown, Award, AlertTriangle,
  Plus, ClipboardList, Star, ChevronRight, UserCheck, Briefcase,
  ArrowUpRight, ArrowDownRight, Minus, Eye
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const RATING_COLORS = {
  exceptional: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  exceeds_expectations: 'bg-blue-100 text-blue-700 border-blue-300',
  meets_expectations: 'bg-amber-100 text-amber-700 border-amber-300',
  needs_improvement: 'bg-orange-100 text-orange-700 border-orange-300',
  unsatisfactory: 'bg-red-100 text-red-700 border-red-300',
};
const RATING_LABELS = {
  exceptional: 'Exceptional',
  exceeds_expectations: 'Exceeds Expectations',
  meets_expectations: 'Meets Expectations',
  needs_improvement: 'Needs Improvement',
  unsatisfactory: 'Unsatisfactory',
};

const DEPARTMENTS = ['sales', 'marketing', 'support', 'operations', 'management', 'general'];
const LEVELS = [
  { value: 'individual_contributor', label: 'Individual Contributor' },
  { value: 'team_lead', label: 'Team Lead' },
  { value: 'manager', label: 'Manager' },
  { value: 'senior_manager', label: 'Senior Manager' },
  { value: 'director', label: 'Director' },
  { value: 'vp', label: 'Vice President' },
  { value: 'c_level', label: 'C-Level' },
];

function generatePeriods() {
  const periods = [];
  const y = new Date().getFullYear();
  for (let yr = y - 1; yr <= y + 1; yr++) {
    for (let q = 1; q <= 4; q++) periods.push(`Q${q} ${yr}`);
    for (let m = 1; m <= 12; m++) periods.push(`${yr}-${String(m).padStart(2, '0')}`);
    periods.push(`FY ${yr}`);
  }
  return periods;
}
const PERIODS = generatePeriods();

export default function PerformanceEvaluationPage() {
  const [tab, setTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(`Q${Math.ceil((new Date().getMonth() + 1) / 3)} ${new Date().getFullYear()}`);
  const [periodType, setPeriodType] = useState('quarterly');
  const [evalModal, setEvalModal] = useState(null);
  const [empModal, setEmpModal] = useState(null);
  const [detailModal, setDetailModal] = useState(null);
  // Goals & Departments
  const [goals, setGoals] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [goalModal, setGoalModal] = useState(null);
  const [deptModal, setDeptModal] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [dashRes, empRes, evalRes, tplRes, goalsRes, deptRes] = await Promise.all([
        axios.get(`${API}/api/performance/dashboard?period=${period}&period_type=${periodType}`),
        axios.get(`${API}/api/performance/employees`),
        axios.get(`${API}/api/performance/evaluations?period=${period}`),
        axios.get(`${API}/api/performance/kpi-templates`),
        axios.get(`${API}/api/performance/goals`),
        axios.get(`${API}/api/performance/departments`),
      ]);
      setDashboard(dashRes.data);
      setEmployees(empRes.data.employees || []);
      setEvaluations(evalRes.data.evaluations || []);
      setTemplates(tplRes.data.defaults || {});
      setGoals(goalsRes.data.goals || []);
      setDepartments(deptRes.data.departments || []);
    } catch { /* */ } finally { setLoading(false); }
  }, [period, periodType]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const s = dashboard?.summary || {};

  return (
    <div className="space-y-5 animate-fadeIn" data-testid="performance-page">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">
            Performance Evaluation<PageInfoTooltip pageKey="performance" />
          </h1>
          <p className="text-gray-medium text-sm mt-1">KPI-driven performance tracking and evaluations</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={periodType} onValueChange={setPeriodType}>
            <SelectTrigger className="h-8 w-28 text-xs" data-testid="period-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="quarterly">Quarterly</SelectItem>
              <SelectItem value="yearly">Yearly</SelectItem>
            </SelectContent>
          </Select>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="h-8 w-32 text-xs" data-testid="period-select"><SelectValue /></SelectTrigger>
            <SelectContent>{PERIODS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" className="h-8 text-xs gap-1" onClick={() => setEvalModal({ employee_id: '', scores: {}, overall_score: 0 })} data-testid="new-evaluation-btn">
            <Plus className="h-3.5 w-3.5" />New Evaluation
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-gray-border w-full sm:w-auto overflow-x-auto flex-nowrap justify-start">
          <TabsTrigger value="dashboard" className="text-xs gap-1.5" data-testid="tab-dashboard"><BarChart3 className="h-3.5 w-3.5" />Dashboard</TabsTrigger>
          <TabsTrigger value="employees" className="text-xs gap-1.5" data-testid="tab-employees"><Users className="h-3.5 w-3.5" />Employees</TabsTrigger>
          <TabsTrigger value="evaluations" className="text-xs gap-1.5" data-testid="tab-evaluations"><ClipboardList className="h-3.5 w-3.5" />Evaluations</TabsTrigger>
          <TabsTrigger value="kpis" className="text-xs gap-1.5" data-testid="tab-kpis"><Target className="h-3.5 w-3.5" />KPI Templates</TabsTrigger>
          <TabsTrigger value="goals" className="text-xs gap-1.5" data-testid="tab-goals"><Award className="h-3.5 w-3.5" />Goals</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard">
          <DashboardView dashboard={dashboard} loading={loading} onViewEmployee={id => setDetailModal(id)} />
        </TabsContent>
        <TabsContent value="employees">
          <EmployeeList employees={employees} evaluations={evaluations} onEvaluate={e => setEvalModal({ employee_id: e.employee_id, employee_name: e.name, scores: {}, overall_score: 0 })} onViewHistory={id => setDetailModal(id)} />
        </TabsContent>
        <TabsContent value="evaluations">
          <EvaluationList evaluations={evaluations} onView={e => setDetailModal(e.employee_id)} onNewEval={() => setEvalModal({ employee_id: '', scores: {}, overall_score: 0 })} />
        </TabsContent>
        <TabsContent value="kpis">
          <KPITemplatesView templates={templates} />
        </TabsContent>
        <TabsContent value="goals">
          <GoalsView goals={goals} employees={employees} departments={departments}
            onNewGoal={() => setGoalModal({ assign_type: 'person', title: '', target_value: 0, current_value: 0 })}
            onNewDept={() => setDeptModal({ name: '' })}
            onRefresh={fetchAll} />
        </TabsContent>
      </Tabs>

      {evalModal && <EvaluationDialog open={!!evalModal} data={evalModal} employees={employees} templates={templates} period={period} periodType={periodType} onClose={() => setEvalModal(null)} onSaved={() => { setEvalModal(null); fetchAll(); }} />}
      {empModal && <EmployeeDialog open={!!empModal} data={empModal} employees={employees} onClose={() => setEmpModal(null)} onSaved={() => { setEmpModal(null); fetchAll(); }} />}
      {detailModal && <EmployeeDetailDialog open={!!detailModal} employeeId={detailModal} onClose={() => setDetailModal(null)} />}
      {goalModal && <GoalDialog open={!!goalModal} data={goalModal} employees={employees} departments={departments} onClose={() => setGoalModal(null)} onSaved={() => { setGoalModal(null); fetchAll(); }} />}
      {deptModal && <DeptDialog open={!!deptModal} data={deptModal} onClose={() => setDeptModal(null)} onSaved={() => { setDeptModal(null); fetchAll(); }} />}
    </div>
  );
}

// ─── Dashboard View ───
function DashboardView({ dashboard, loading, onViewEmployee }) {
  if (loading) return <div className="text-center py-12 text-gray-400">Loading...</div>;
  const s = dashboard?.summary || {};
  const TrendIcon = ({ trend }) => trend === 'improving' ? <ArrowUpRight className="h-3.5 w-3.5 text-emerald-500" /> : trend === 'declining' ? <ArrowDownRight className="h-3.5 w-3.5 text-red-500" /> : <Minus className="h-3.5 w-3.5 text-gray-400" />;

  return (
    <div className="space-y-4" data-testid="performance-dashboard">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Users} iconColor="text-blue-500" bg="bg-blue-50" label="Total Employees" value={s.total_employees || 0} />
        <StatCard icon={UserCheck} iconColor="text-emerald-500" bg="bg-emerald-50" label="Evaluated" value={`${s.total_evaluated || 0} (${s.evaluation_coverage || 0}%)`} />
        <StatCard icon={BarChart3} iconColor="text-amber-500" bg="bg-amber-50" label="Avg Score" value={`${s.avg_score || 0}/100`} />
        <StatCard icon={Award} iconColor="text-purple-500" bg="bg-purple-50" label="Company Rating" value={RATING_LABELS[s.company_rating] || 'N/A'} />
      </div>

      {/* Rating Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="bg-white border-gray-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Rating Distribution</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {Object.entries(RATING_LABELS).map(([key, label]) => {
              const count = dashboard?.rating_distribution?.[key] || 0;
              const total = s.total_evaluated || 1;
              return (
                <div key={key} className="flex items-center gap-2" data-testid={`rating-${key}`}>
                  <Badge className={cn('text-[10px] w-32 justify-center border', RATING_COLORS[key])}>{label}</Badge>
                  <Progress value={(count / total) * 100} className="flex-1 h-2" />
                  <span className="text-xs font-medium w-8 text-right">{count}</span>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Department Leaderboard */}
        <Card className="bg-white border-gray-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Department Leaderboard</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {(dashboard?.departments || []).map((d, i) => (
              <div key={d.department} className="flex items-center gap-3 p-2 rounded-lg bg-gray-50/50" data-testid={`dept-${d.department}`}>
                <span className={cn("w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold",
                  i === 0 ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-500")}>{i + 1}</span>
                <div className="flex-1">
                  <p className="text-sm font-medium capitalize">{d.department}</p>
                  <p className="text-[10px] text-gray-400">{d.evaluated} evaluated</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold">{d.avg_score}/100</p>
                  {d.top_performer && <p className="text-[10px] text-emerald-600">Top: {d.top_performer.name}</p>}
                </div>
              </div>
            ))}
            {(!dashboard?.departments || dashboard.departments.length === 0) && <p className="text-xs text-gray-400 text-center py-4">No evaluations yet for this period</p>}
          </CardContent>
        </Card>
      </div>

      {/* Top Performers & Needs Attention */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="bg-white border-gray-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Star className="h-4 w-4 text-amber-500" />Top Performers</CardTitle></CardHeader>
          <CardContent className="space-y-1.5">
            {(dashboard?.top_performers || []).map((p, i) => (
              <div key={i} className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50">
                <Award className={cn("h-4 w-4", i === 0 ? "text-amber-500" : "text-gray-300")} />
                <span className="text-sm flex-1">{p.name}</span>
                <span className="text-xs font-bold">{p.score}/100</span>
                <Badge className={cn("text-[9px] border", RATING_COLORS[p.rating])}>{RATING_LABELS[p.rating]}</Badge>
              </div>
            ))}
            {(!dashboard?.top_performers?.length) && <p className="text-xs text-gray-400 text-center py-3">No data yet</p>}
          </CardContent>
        </Card>

        <Card className="bg-white border-gray-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-orange-500" />Needs Attention</CardTitle></CardHeader>
          <CardContent className="space-y-1.5">
            {(dashboard?.needs_attention || []).map((p, i) => (
              <div key={i} className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50">
                <AlertTriangle className="h-4 w-4 text-orange-400" />
                <span className="text-sm flex-1">{p.name}</span>
                <span className="text-xs font-bold text-red-600">{p.score}/100</span>
                <Badge className="text-[9px] bg-orange-100 text-orange-700">{p.recommendation}</Badge>
              </div>
            ))}
            {(!dashboard?.needs_attention?.length) && <p className="text-xs text-emerald-600 text-center py-3">All employees performing well!</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, iconColor, bg, label, value }) {
  return (
    <Card className="bg-white border-gray-border">
      <CardContent className="p-3 flex items-center gap-3">
        <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", bg)}><Icon className={cn("h-5 w-5", iconColor)} /></div>
        <div><p className="text-[10px] text-gray-400 uppercase tracking-wide">{label}</p><p className="text-sm font-bold text-text-primary">{value}</p></div>
      </CardContent>
    </Card>
  );
}

// ─── Employee List ───
function EmployeeList({ employees, evaluations, onEvaluate, onViewHistory }) {
  return (
    <div className="space-y-3" data-testid="employee-list">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-400">{employees.length} employees</p>
        <p className="text-[10px] text-gray-400">Auto-synced from Team & People</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {employees.map(e => {
          const latestEval = evaluations.find(ev => ev.employee_id === e.employee_id);
          return (
            <Card key={e.employee_id} className="bg-white border-gray-border hover:shadow-md transition-all" data-testid={`emp-card-${e.employee_id}`}>
              <CardContent className="p-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center text-sm font-bold text-blue-700">
                    {e.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{e.name}</p>
                    <p className="text-[10px] text-gray-400 capitalize">{e.department} · {LEVELS.find(l => l.value === e.level)?.label || e.level}</p>
                    {latestEval ? (
                      <div className="flex items-center gap-1.5 mt-1">
                        <Badge className={cn("text-[9px] border", RATING_COLORS[latestEval.rating])}>{latestEval.overall_score}/100</Badge>
                      </div>
                    ) : (
                      <Badge className="text-[9px] bg-gray-100 text-gray-500 mt-1">Not evaluated</Badge>
                    )}
                  </div>
                </div>
                <div className="flex gap-1.5 mt-3">
                  <Button size="sm" variant="outline" className="flex-1 h-7 text-[10px]" onClick={() => onEvaluate(e)}>
                    <ClipboardList className="h-3 w-3 mr-1" />Evaluate
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onViewHistory(e.employee_id)} data-testid={`view-history-${e.employee_id}`}>
                    <Eye className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ─── Evaluation List ───
function EvaluationList({ evaluations, onView, onNewEval }) {
  if (evaluations.length === 0) {
    return (
      <div className="space-y-2" data-testid="evaluation-list">
        <div className="text-center py-12 border border-dashed border-gray-200 rounded-lg bg-white" data-testid="evaluation-list-empty">
          <ClipboardList className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-slate-700 mb-1">No evaluations for this period</p>
          <p className="text-xs text-gray-400 mb-4 px-6">Score employees against your KPI templates so this dashboard fills with talent insights, top performers and rating trends.</p>
          {onNewEval && (
            <Button size="sm" onClick={onNewEval} data-testid="evaluation-list-empty-cta">
              <Plus className="h-4 w-4 mr-1.5" />Start your first evaluation
            </Button>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="evaluation-list">
      {evaluations.map(e => (
        <Card key={e.evaluation_id} className="bg-white border-gray-border hover:shadow-sm cursor-pointer transition-all" onClick={() => onView(e)} data-testid={`eval-${e.evaluation_id}`}>
          <CardContent className="p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center font-bold text-sm text-indigo-700">
              {e.employee_name?.charAt(0) || '?'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{e.employee_name}</p>
              <p className="text-[10px] text-gray-400">{e.period} · By {e.evaluator_name}</p>
            </div>
            <div className="text-right">
              <p className="text-sm font-bold">{e.overall_score}/100</p>
              <Badge className={cn("text-[9px] border", RATING_COLORS[e.rating])}>{RATING_LABELS[e.rating]}</Badge>
            </div>
            <ChevronRight className="h-4 w-4 text-gray-300" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ─── KPI Templates View ───
function KPITemplatesView({ templates }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="kpi-templates">
      {Object.entries(templates).map(([dept, kpis]) => (
        <Card key={dept} className="bg-white border-gray-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold capitalize flex items-center gap-2">
              <Target className="h-4 w-4 text-blue-500" />{dept}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5">
            {kpis.map(k => (
              <div key={k.code} className="flex items-center gap-2 p-1.5 rounded hover:bg-gray-50" data-testid={`kpi-${k.code}`}>
                <div className="flex-1">
                  <p className="text-xs font-medium">{k.name}</p>
                  <p className="text-[10px] text-gray-400 capitalize">{k.unit} · {k.direction.replace('_', ' ')}</p>
                </div>
                <Badge className="text-[9px] bg-blue-50 text-blue-600">{k.weight}%</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ─── Goals View ───
function GoalsView({ goals, employees, departments, onNewGoal, onNewDept, onRefresh }) {
  const personGoals = goals.filter(g => g.assign_type === 'person');
  const teamGoals = goals.filter(g => g.assign_type === 'team');
  const activeGoals = goals.filter(g => g.status === 'active');
  const completedGoals = goals.filter(g => g.status === 'completed');

  const handleUpdateProgress = async (goal, newValue) => {
    try {
      await axios.put(`${API}/api/performance/goals/${goal.goal_id}`, { current_value: Number(newValue) });
      toast.success('Progress updated');
      onRefresh();
    } catch { toast.error('Failed to update'); }
  };

  return (
    <div className="space-y-4" data-testid="goals-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Badge className="bg-blue-50 text-blue-600 text-xs">{personGoals.length} Person</Badge>
          <Badge className="bg-purple-50 text-purple-600 text-xs">{teamGoals.length} Team</Badge>
          <Badge className="bg-emerald-50 text-emerald-600 text-xs">{completedGoals.length} Completed</Badge>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={onNewDept} data-testid="new-dept-btn">
            <Users className="h-3 w-3" />New Department
          </Button>
          <Button size="sm" className="h-7 text-xs gap-1" onClick={onNewGoal} data-testid="new-goal-btn">
            <Plus className="h-3 w-3" />New Goal
          </Button>
        </div>
      </div>

      {departments.length > 0 && (
        <Card className="bg-white border-gray-border">
          <CardContent className="p-3">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-2 font-medium">Departments / Teams</p>
            <div className="flex flex-wrap gap-2">
              {departments.map(d => (
                <Badge key={d.department_id} className="bg-slate-100 text-slate-700 text-xs px-2.5 py-1" data-testid={`dept-${d.department_id}`}>
                  <Briefcase className="h-3 w-3 mr-1" />{d.name}
                  {d.member_ids?.length > 0 && <span className="ml-1 text-gray-400">({d.member_ids.length})</span>}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {activeGoals.length === 0 && completedGoals.length === 0 && (
        <Card className="bg-white border-gray-border">
          <CardContent className="p-8 text-center">
            <Target className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No goals created yet</p>
            <p className="text-xs text-gray-400 mt-1">Create goals for individuals or teams to track KPI progress</p>
          </CardContent>
        </Card>
      )}

      {activeGoals.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-600">Active Goals</p>
          <div className="grid gap-2">
            {activeGoals.map(g => (
              <Card key={g.goal_id} className="bg-white border-gray-border" data-testid={`goal-${g.goal_id}`}>
                <CardContent className="p-3 flex items-center gap-3">
                  <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center shrink-0",
                    g.assign_type === 'team' ? "bg-purple-100" : "bg-blue-100")}>
                    {g.assign_type === 'team' ? <Users className="h-4 w-4 text-purple-600" /> : <UserCheck className="h-4 w-4 text-blue-600" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium truncate">{g.title}</p>
                      <Badge className={cn("text-[9px] shrink-0", g.assign_type === 'team' ? "bg-purple-50 text-purple-600" : "bg-blue-50 text-blue-600")}>
                        {g.assign_type === 'team' ? 'Team' : 'Person'}
                      </Badge>
                    </div>
                    <p className="text-[10px] text-gray-400">{g.assignee_name || 'Unassigned'} {g.deadline && `· Due: ${g.deadline}`}</p>
                  </div>
                  <div className="w-32 shrink-0">
                    <div className="flex items-center justify-between text-[10px] text-gray-500 mb-0.5">
                      <span>{g.current_value}/{g.target_value} {g.unit}</span>
                      <span>{g.progress || 0}%</span>
                    </div>
                    <Progress value={g.progress || 0} className="h-1.5" />
                  </div>
                  <Input type="number" className="w-16 h-7 text-xs text-center" defaultValue={g.current_value || 0}
                    onBlur={(e) => handleUpdateProgress(g, e.target.value)} data-testid={`goal-progress-${g.goal_id}`} />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {completedGoals.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-600">Completed</p>
          <div className="grid gap-2">
            {completedGoals.map(g => (
              <Card key={g.goal_id} className="bg-gray-50/50 border-gray-border opacity-70" data-testid={`goal-done-${g.goal_id}`}>
                <CardContent className="p-3 flex items-center gap-3">
                  <Award className="h-5 w-5 text-emerald-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate line-through text-gray-500">{g.title}</p>
                    <p className="text-[10px] text-gray-400">{g.assignee_name}</p>
                  </div>
                  <Badge className="bg-emerald-50 text-emerald-600 text-[9px]">Completed</Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Goal Dialog ───
function GoalDialog({ open, data, employees, departments, onClose, onSaved }) {
  const [form, setForm] = useState({ ...data });
  const [saving, setSaving] = useState(false);

  const assignees = form.assign_type === 'team'
    ? departments.map(d => ({ id: d.department_id, name: d.name }))
    : employees.map(e => ({ id: e.employee_id, name: e.employee_name || e.name }));

  const handleSave = async () => {
    if (!form.title?.trim()) { toast.error('Goal title required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/performance/goals`, form);
      toast.success('Goal created');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="goal-dialog">
        <DialogHeader><DialogTitle>Create Goal</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2 p-1 bg-gray-100 rounded-lg" data-testid="goal-assign-toggle">
            <button onClick={() => setForm(f => ({ ...f, assign_type: 'person', assignee_id: '', assignee_name: '' }))}
              className={cn("flex-1 py-1.5 rounded text-xs font-medium transition-all flex items-center justify-center gap-1",
                form.assign_type === 'person' ? "bg-white shadow text-blue-600" : "text-gray-500")}>
              <UserCheck className="h-3 w-3" />Person
            </button>
            <button onClick={() => setForm(f => ({ ...f, assign_type: 'team', assignee_id: '', assignee_name: '' }))}
              className={cn("flex-1 py-1.5 rounded text-xs font-medium transition-all flex items-center justify-center gap-1",
                form.assign_type === 'team' ? "bg-white shadow text-purple-600" : "text-gray-500")}>
              <Users className="h-3 w-3" />Team
            </button>
          </div>

          <div>
            <Label className="text-xs">Goal Title *</Label>
            <Input placeholder="e.g. Close 50 deals this quarter" value={form.title || ''} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              className="mt-1" data-testid="goal-title-input" />
          </div>

          <div>
            <Label className="text-xs">Assign To</Label>
            <Select value={form.assignee_id || ''} onValueChange={v => {
              const match = assignees.find(a => a.id === v);
              setForm(f => ({ ...f, assignee_id: v, assignee_name: match?.name || '' }));
            }}>
              <SelectTrigger className="mt-1 h-8 text-xs" data-testid="goal-assignee-select"><SelectValue placeholder={`Select ${form.assign_type}`} /></SelectTrigger>
              <SelectContent>
                {assignees.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Target Value</Label>
              <Input type="number" value={form.target_value || ''} onChange={e => setForm(f => ({ ...f, target_value: Number(e.target.value) }))}
                className="mt-1" data-testid="goal-target-input" />
            </div>
            <div>
              <Label className="text-xs">Unit</Label>
              <Select value={form.unit || 'count'} onValueChange={v => setForm(f => ({ ...f, unit: v }))}>
                <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="count">Count</SelectItem>
                  <SelectItem value="currency">Currency</SelectItem>
                  <SelectItem value="percentage">Percentage</SelectItem>
                  <SelectItem value="hours">Hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs">Deadline (optional)</Label>
            <Input type="date" value={form.deadline || ''} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))}
              className="mt-1" data-testid="goal-deadline-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-goal-btn">
            {saving ? 'Saving...' : 'Create Goal'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─── Department Dialog ───
function DeptDialog({ open, data, onClose, onSaved }) {
  const [name, setName] = useState(data?.name || '');
  const [desc, setDesc] = useState(data?.description || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Department name required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/performance/departments`, { name, description: desc });
      toast.success('Department created');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="dept-dialog">
        <DialogHeader><DialogTitle>Create Department / Team</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Department Name *</Label>
            <Input placeholder="e.g. Sales Team" value={name} onChange={e => setName(e.target.value)}
              className="mt-1" data-testid="dept-name-input" />
          </div>
          <div>
            <Label className="text-xs">Description (optional)</Label>
            <Input placeholder="Brief description" value={desc} onChange={e => setDesc(e.target.value)} className="mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-dept-btn">{saving ? 'Saving...' : 'Create Department'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─── Evaluation Dialog ───
function EvaluationDialog({ open, data, employees, templates, period, periodType, onClose, onSaved }) {
  const [form, setForm] = useState({ ...data, period, period_type: periodType });
  const [scores, setScores] = useState({});
  const [saving, setSaving] = useState(false);

  const selectedEmp = employees.find(e => e.employee_id === form.employee_id);
  const dept = selectedEmp?.role_category || selectedEmp?.department || 'general';
  const kpis = templates[dept] || templates.general || [];

  useEffect(() => {
    const total = kpis.reduce((sum, k) => {
      const s = scores[k.code] || 0;
      return sum + (s * k.weight / 100);
    }, 0);
    setForm(f => ({ ...f, overall_score: Math.round(total), scores }));
  }, [scores, kpis]);

  const save = async (status = 'draft') => {
    if (!form.employee_id) { toast.error('Select an employee'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/performance/evaluations`, {
        ...form,
        employee_name: selectedEmp?.name || '',
        status,
      });
      toast.success(status === 'submitted' ? 'Evaluation submitted!' : 'Draft saved');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="evaluation-dialog">
        <DialogHeader><DialogTitle className="text-base">New Performance Evaluation</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Employee</Label>
              <Select value={form.employee_id} onValueChange={v => setForm(f => ({ ...f, employee_id: v }))}>
                <SelectTrigger className="mt-1" data-testid="eval-employee"><SelectValue placeholder="Select employee..." /></SelectTrigger>
                <SelectContent>{employees.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.name} ({e.department})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Period</Label>
              <Select value={form.period} onValueChange={v => setForm(f => ({ ...f, period: v }))}>
                <SelectTrigger className="mt-1" data-testid="eval-period"><SelectValue /></SelectTrigger>
                <SelectContent>{PERIODS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {/* KPI Scoring */}
          {form.employee_id && (
            <Card className="bg-gray-50 border-gray-border">
              <CardHeader className="pb-2"><CardTitle className="text-xs font-semibold capitalize">{dept} KPIs</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {kpis.map(k => (
                  <div key={k.code} className="space-y-1" data-testid={`score-${k.code}`}>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">{k.name} <span className="text-gray-400">({k.weight}%)</span></Label>
                      <span className="text-xs font-bold">{scores[k.code] || 0}/100</span>
                    </div>
                    <Slider value={[scores[k.code] || 0]} onValueChange={([v]) => setScores(s => ({ ...s, [k.code]: v }))} max={100} step={1} className="w-full" />
                  </div>
                ))}
                <div className="pt-2 border-t border-gray-200 flex items-center justify-between">
                  <span className="text-sm font-bold">Overall Score</span>
                  <span className="text-lg font-bold">{form.overall_score}/100</span>
                </div>
              </CardContent>
            </Card>
          )}

          <div>
            <Label className="text-xs">Strengths</Label>
            <Textarea value={form.strengths || ''} onChange={e => setForm(f => ({ ...f, strengths: e.target.value }))} placeholder="Key strengths and achievements..." className="mt-1 min-h-[60px] text-sm" data-testid="eval-strengths" />
          </div>
          <div>
            <Label className="text-xs">Areas for Improvement</Label>
            <Textarea value={form.improvements || ''} onChange={e => setForm(f => ({ ...f, improvements: e.target.value }))} placeholder="Areas needing development..." className="mt-1 min-h-[60px] text-sm" data-testid="eval-improvements" />
          </div>
          <div>
            <Label className="text-xs">Goals for Next Period</Label>
            <Textarea value={form.goals_next_period || ''} onChange={e => setForm(f => ({ ...f, goals_next_period: e.target.value }))} placeholder="Objectives and targets..." className="mt-1 min-h-[60px] text-sm" data-testid="eval-goals" />
          </div>
          <div>
            <Label className="text-xs">Recommendation</Label>
            <Select value={form.recommendation || 'maintain'} onValueChange={v => setForm(f => ({ ...f, recommendation: v }))}>
              <SelectTrigger className="mt-1" data-testid="eval-recommendation"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="promote">Promote</SelectItem>
                <SelectItem value="salary_increase">Salary Increase</SelectItem>
                <SelectItem value="maintain">Maintain Current Position</SelectItem>
                <SelectItem value="performance_plan">Performance Improvement Plan</SelectItem>
                <SelectItem value="warning">Formal Warning</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => save('draft')} disabled={saving} data-testid="save-draft-btn">Save Draft</Button>
          <Button onClick={() => save('submitted')} disabled={saving} data-testid="submit-eval-btn">Submit Evaluation</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Employee Dialog ───
function EmployeeDialog({ open, data, employees, onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', email: '', department: 'general', role_category: 'general', level: 'individual_contributor', manager_id: '', ...data });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/performance/employees`, form);
      toast.success('Employee saved');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="employee-dialog">
        <DialogHeader><DialogTitle className="text-base">{form.employee_id ? 'Edit Employee' : 'Add Employee'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Full Name</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="John Doe" className="mt-1 text-sm" data-testid="emp-name" /></div>
            <div><Label className="text-xs">Email</Label><Input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="john@company.com" className="mt-1 text-sm" data-testid="emp-email" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Department</Label>
              <Select value={form.department} onValueChange={v => setForm(f => ({ ...f, department: v, role_category: v }))}><SelectTrigger className="mt-1" data-testid="emp-department"><SelectValue /></SelectTrigger>
                <SelectContent>{DEPARTMENTS.map(d => <SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs">Level</Label>
              <Select value={form.level} onValueChange={v => setForm(f => ({ ...f, level: v }))}><SelectTrigger className="mt-1" data-testid="emp-level"><SelectValue /></SelectTrigger>
                <SelectContent>{LEVELS.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div><Label className="text-xs">Reports To</Label>
            <Select value={form.manager_id || 'none'} onValueChange={v => setForm(f => ({ ...f, manager_id: v === 'none' ? null : v }))}>
              <SelectTrigger className="mt-1" data-testid="emp-manager"><SelectValue placeholder="Select manager..." /></SelectTrigger>
              <SelectContent><SelectItem value="none">No Manager</SelectItem>{employees.filter(e => e.employee_id !== form.employee_id).map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.name}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="save-employee-btn">{form.employee_id ? 'Update' : 'Add'} Employee</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Employee Detail Dialog ───
function EmployeeDetailDialog({ open, employeeId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!employeeId) return;
    axios.get(`${API}/api/performance/employee/${employeeId}/history`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [employeeId]);

  if (loading) return <Dialog open={open} onOpenChange={onClose}><DialogContent><p className="text-sm text-center py-8">Loading...</p></DialogContent></Dialog>;

  const emp = data?.employee || {};
  const evals = data?.evaluations || [];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="employee-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center font-bold text-sm text-blue-700">{emp.name?.charAt(0) || '?'}</div>
            {emp.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className="text-xs capitalize bg-blue-50 text-blue-700">{emp.department}</Badge>
            <Badge className="text-xs bg-gray-50 text-gray-600">{LEVELS.find(l => l.value === emp.level)?.label}</Badge>
            {data?.trend && (
              <Badge className={cn("text-xs", data.trend === 'improving' ? 'bg-emerald-50 text-emerald-700' : data.trend === 'declining' ? 'bg-red-50 text-red-700' : 'bg-gray-50 text-gray-600')}>
                {data.trend === 'improving' ? <TrendingUp className="h-3 w-3 mr-1" /> : data.trend === 'declining' ? <TrendingDown className="h-3 w-3 mr-1" /> : null}
                {data.trend}
              </Badge>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Card className="bg-gray-50 border-gray-border"><CardContent className="p-3"><p className="text-[10px] text-gray-400">Average Score</p><p className="text-xl font-bold">{data?.avg_score || 0}/100</p></CardContent></Card>
            <Card className="bg-gray-50 border-gray-border"><CardContent className="p-3"><p className="text-[10px] text-gray-400">Latest Score</p><p className="text-xl font-bold">{data?.latest_score || 'N/A'}</p></CardContent></Card>
          </div>

          <div>
            <h3 className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">Evaluation History</h3>
            {evals.length === 0 && <p className="text-xs text-gray-400 text-center py-4">No evaluations yet</p>}
            {evals.map(e => (
              <div key={e.evaluation_id} className="border border-gray-border rounded-lg p-3 mb-2" data-testid={`history-eval-${e.evaluation_id}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">{e.period}</span>
                  <Badge className={cn("text-[9px] border", RATING_COLORS[e.rating])}>{e.overall_score}/100</Badge>
                </div>
                {e.strengths && <p className="text-xs text-gray-600 mb-1"><span className="font-medium text-gray-800">Strengths:</span> {e.strengths}</p>}
                {e.improvements && <p className="text-xs text-gray-600 mb-1"><span className="font-medium text-gray-800">Improve:</span> {e.improvements}</p>}
                {e.goals_next_period && <p className="text-xs text-gray-600"><span className="font-medium text-gray-800">Goals:</span> {e.goals_next_period}</p>}
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
